import { useCallback, useEffect, useState } from 'react';

import { performCoachSignalChecks } from 'bundles/front-page/utils/coachDecisionHesitationUtils';
import type { Viewed } from 'bundles/search-common/types/autocomplete';

export type LOHPCoachDecisionHesitationTriggerResult = {
  shouldShowCoach: boolean;
  viewedXDPs: Viewed[];
  isFirstTimeTrigger: boolean;
};

/**
 * Hook to determine if Coach prompt pills should be shown based on Coach decision hesitation experiment logic.
 *
 * @returns {shouldShowCoach, viewedXDPs, isFirstTimeTrigger}
 */
export const useLOHPCoachDecisionHesitationTrigger = (): LOHPCoachDecisionHesitationTriggerResult => {
  const [shouldShowCoach, setShouldShowCoach] = useState(false);
  const [viewedXDPs, setViewedXDPs] = useState<Viewed[]>([]);
  const [isFirstTimeTrigger, setIsFirstTimeTrigger] = useState(false);

  const performSignalChecks = useCallback<() => void>(() => {
    const result = performCoachSignalChecks();

    setShouldShowCoach(result.shouldShow);
    setViewedXDPs(result.viewedXDPs);
    setIsFirstTimeTrigger(result.isFirstTimeTrigger);
  }, []);

  useEffect(() => {
    performSignalChecks();
  }, [performSignalChecks]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        performSignalChecks();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [performSignalChecks]);

  return { shouldShowCoach, viewedXDPs, isFirstTimeTrigger };
};
